import { store, getElement } from '@wordpress/interactivity';
import blockDef from '../Container/block.json';
import { blockNameToClassName } from '../../../../Utils/class-names';

// The class core puts on the block, after our prefix-stripping filter.
const BLOCK_CLASS_NAME = blockNameToClassName(blockDef.name);

// Attribute defaults are defined in block.json only; derive them here so the
// editor and the front-end view can never drift apart.
const DEFAULT_TRANSITION_DURATION =
  blockDef.attributes.transitionDuration.default;
const DEFAULT_THRESHOLD = blockDef.attributes.threshold.default;

// Track currently active color shift section
let activeColorShiftSection = null;

// Track pending timeout for cleanup
let cleanupTimeout = null;

// Global tracking of observed elements to prevent duplicate observers
const observedElements = new Set();

// Global IntersectionObserver (created lazily)
let globalObserver = null;

// Global MutationObserver (created lazily)
let globalMutationObserver = null;

// Helper to safely parse JSON attributes
const parseColorShiftContext = (element) => {
  try {
    const ctxAttr = element.getAttribute('data-wp-context');
    return ctxAttr ? JSON.parse(ctxAttr) : {};
  } catch (error) {
    console.error('Failed to parse color shift context:', error, element);
    return {};
  }
};

// A block only takes part in the color shift when it actually defines a color
// to shift to. An unconfigured block (no colors set) is inert: it must never
// win arbitration, and it must never deactivate the section that is currently
// active.
const isConfiguredColorShift = (element) => {
  const { sectionTextColor, sectionBackgroundColor } =
    parseColorShiftContext(element);

  return Boolean(sectionTextColor || sectionBackgroundColor);
};

// Apply colors to the page
const applyColors = (
  backgroundColor,
  textColor,
  transitionDuration = DEFAULT_TRANSITION_DURATION
) => {
  const body = document.body;

  // Clear any pending cleanup
  if (cleanupTimeout) {
    clearTimeout(cleanupTimeout);
    cleanupTimeout = null;
  }

  // Apply transition and colors immediately
  body.style.transition = `background-color ${transitionDuration}s ease, color ${transitionDuration}s ease`;

  if (backgroundColor) {
    body.style.backgroundColor = backgroundColor;
  }
  if (textColor) {
    body.style.color = textColor;
  }
};

// Remove inline styles to revert to CSS-defined colors
const removeInlineStyles = (
  transitionDuration = DEFAULT_TRANSITION_DURATION
) => {
  const body = document.body;

  // Clear any pending cleanup
  if (cleanupTimeout) {
    clearTimeout(cleanupTimeout);
    cleanupTimeout = null;
  }

  // Keep transition active for smooth fade
  body.style.transition = `background-color ${transitionDuration}s ease, color ${transitionDuration}s ease`;

  // Remove the color properties to let CSS take over
  body.style.removeProperty('background-color');
  body.style.removeProperty('color');

  // Remove transition after it completes
  cleanupTimeout = setTimeout(() => {
    body.style.removeProperty('transition');
    cleanupTimeout = null;
  }, transitionDuration * 1000);
};

// Mark element as active and apply colors
const activateColorShift = (element) => {
  const ctx = parseColorShiftContext(element);
  const {
    sectionTextColor,
    sectionBackgroundColor,
    transitionDuration = DEFAULT_TRANSITION_DURATION,
  } = ctx;

  // Defensive guard: unconfigured blocks are already excluded from arbitration,
  // so this should never be reached.
  if (!sectionTextColor && !sectionBackgroundColor) return;

  // If there's already an active section, it will be deactivated
  if (activeColorShiftSection && activeColorShiftSection !== element) {
    activeColorShiftSection.classList.remove('is-active-color-shift');
  }

  // Set this element as the active one
  activeColorShiftSection = element;
  element.classList.add('is-active-color-shift');

  // Apply the colors
  applyColors(sectionBackgroundColor, sectionTextColor, transitionDuration);
};

// Deactivate color shift
const deactivateColorShift = (element) => {
  const ctx = parseColorShiftContext(element);
  const { transitionDuration = DEFAULT_TRANSITION_DURATION } = ctx;

  element.classList.remove('is-active-color-shift');

  // Only revert if this was the active section
  if (activeColorShiftSection === element) {
    activeColorShiftSection = null;
    removeInlineStyles(transitionDuration);
  }
};

// Drop sections that are no longer in the document. Without this, a replaced
// region would leave its elements in observedElements (a leak) and a detached
// section could keep winning arbitration with a stale intersection ratio,
// leaving the page stuck on its colors.
const pruneDetachedColorShiftSections = () => {
  observedElements.forEach((element) => {
    if (element.isConnected) return;

    globalObserver.unobserve(element);
    observedElements.delete(element);

    // The removed section may be the one currently painting the page.
    if (activeColorShiftSection === element) {
      deactivateColorShift(element);
    }
  });
};

// Create the global IntersectionObserver
const createGlobalObserver = () => {
  if (globalObserver) return globalObserver;

  globalObserver = new IntersectionObserver(
    (entries) => {
      // Process each entry to update their state
      entries.forEach((entry) => {
        const element = entry.target;
        const ctx = parseColorShiftContext(element);
        const threshold = !isNaN(parseFloat(ctx.threshold))
          ? parseFloat(ctx.threshold)
          : DEFAULT_THRESHOLD;

        // Check if element meets threshold - with small tolerance for rounding
        const meetsThreshold =
          entry.isIntersecting && entry.intersectionRatio >= threshold - 0.01;

        // Store the intersection state on the element
        element.__colorShiftIntersectionRatio = meetsThreshold
          ? entry.intersectionRatio
          : 0;
        element.__colorShiftThreshold = threshold;
      });

      // Now find the best candidate from ALL observed elements
      let bestCandidate = null;
      let bestRatio = 0;

      observedElements.forEach((element) => {
        // A block with no colors configured is inert: it must not compete for the
        // active slot, or it would revert the page while it held it.
        if (!element.__colorShiftIsConfigured) return;

        // Backstop for the pruning sweep: a section that is no longer in the
        // document must never win the active slot, even if it still carries a
        // stale intersection ratio.
        if (!element.isConnected) return;

        const ratio = element.__colorShiftIntersectionRatio || 0;
        const threshold = element.__colorShiftThreshold || 0;

        // Element must meet its threshold to be considered
        if (ratio >= threshold && ratio > bestRatio) {
          bestCandidate = element;
          bestRatio = ratio;
        }
      });

      // Only proceed if the best candidate has changed
      if (bestCandidate !== activeColorShiftSection) {
        // Deactivate current if exists
        if (activeColorShiftSection) {
          deactivateColorShift(activeColorShiftSection);
        }

        // Activate new best candidate (only if it has a valid ratio > 0)
        if (bestCandidate && bestRatio > 0) {
          activateColorShift(bestCandidate);
        }
      }
    },
    {
      threshold: [
        0, 0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.99, 1,
      ],
    }
  );

  return globalObserver;
};

// Main function to observe color shift blocks
const observeColorShiftBlocks = () => {
  const observer = createGlobalObserver();

  // Sections are detached and replaced whenever a region is re-rendered, so
  // sweep before (re)scanning for new ones.
  pruneDetachedColorShiftSections();

  // Matches v1 and v2 content alike: v1 saved the shortened class alongside the
  // legacy `wp-block-` prefixed one, v2 saves the shortened class on its own.
  const selector = `.${BLOCK_CLASS_NAME}`;
  const elements = document.querySelectorAll(selector);

  elements.forEach((element) => {
    // Skip if already being observed
    if (observedElements.has(element)) return;

    // Cache whether this block defines any colors, so the observer callback
    // does not have to re-parse the context for every observed element.
    // Configuration only changes when the post is re-saved, which reloads the
    // page.
    element.__colorShiftIsConfigured = isConfiguredColorShift(element);

    // Add to tracking set
    observedElements.add(element);

    // Start observing
    observer.observe(element);

    // Don't activate on page load - let the IntersectionObserver handle it
    // This prevents immediate color changes before scrolling
  });
};

// Initialize mutation observer to watch for dynamically added elements
const initMutationObserver = () => {
  if (globalMutationObserver) return;

  globalMutationObserver = new MutationObserver(() => {
    // Re-run observation setup for any new elements
    observeColorShiftBlocks();
  });

  globalMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// Interactivity API store for ColorShift blocks
store('amplify_storytelling_colorShift', {
  state: {},
  actions: {},
  callbacks: {
    init: () => {
      const el = getElement()?.ref;
      if (!el) return;

      // Use unified initialization
      observeColorShiftBlocks();

      // Ensure mutation observer is running
      initMutationObserver();
    },
  },
});

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    observeColorShiftBlocks();
    initMutationObserver();
  });
} else {
  // DOM already loaded, initialize immediately
  observeColorShiftBlocks();
  initMutationObserver();
}
